import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import * as LucideIcons from 'lucide-react';
import api from '../utils/api';

// Convert kebab-case to PascalCase for Lucide icons
const toPascalCase = (str) => {
  if (!str) return '';
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

const DynamicIcon = ({ name, className }) => {
  const pascalName = toPascalCase(name);
  const IconComponent = LucideIcons[pascalName] || LucideIcons[name] || LucideIcons.FileText;
  return <IconComponent className={className} />;
};

// Color mappings for tags and borders
const COLOR_CLASSES = {
  blue: { border: 'border-t-blue-500', tag: 'bg-blue-100 text-blue-700', label: 'text-blue-600' },
  orange: { border: 'border-t-orange-500', tag: 'bg-orange-100 text-orange-700', label: 'text-orange-600' },
  green: { border: 'border-t-green-500', tag: 'bg-green-100 text-green-700', label: 'text-green-600' },
  purple: { border: 'border-t-purple-500', tag: 'bg-purple-100 text-purple-700', label: 'text-purple-600' },
  cyan: { border: 'border-t-cyan-500', tag: 'bg-cyan-100 text-cyan-700', label: 'text-cyan-600' },
  red: { border: 'border-t-red-500', tag: 'bg-red-100 text-red-700', label: 'text-red-600' },
};

const FeaturesPage = () => {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuresRes, settingsRes] = await Promise.all([
          api.get('/cms/public/features'),
          api.get('/cms/public/site-settings')
        ]);
        setFeatures(featuresRes.data);
        
        // Process settings to convert relative URLs to absolute
        const processedSettings = { ...settingsRes.data };
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Helper to convert URLs
        const convertUrl = (url) => {
          if (!url) return url;
          if (url.startsWith('/api')) return `${backendUrl}${url}`;
          if (url.includes('drive.google.com/file/d/')) {
            const fileId = url.match(/\/d\/([^/]+)/)?.[1];
            if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
          }
          return url;
        };
        
        processedSettings.logo_url = convertUrl(processedSettings.logo_url);
        setSettings(processedSettings);
      } catch (error) {
        console.error('Failed to fetch features:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get unique categories from features
  const categories = useMemo(() => {
    const cats = new Set(features.map(f => f.category).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [features]);

  // Filter features by selected category
  const filteredFeatures = useMemo(() => {
    if (selectedCategory === 'All') return features;
    return features.filter(f => f.category === selectedCategory);
  }, [features, selectedCategory]);

  const siteName = settings?.site_name || 'Ontrof';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="features-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <LucideIcons.Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-white">{siteName}</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-white text-sm font-medium transition-colors">Features</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Pricing</Link>
              <Link to="/contact" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/register')}
                data-testid="nav-register-btn"
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40 hover:scale-105"
              >
                Register
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 font-semibold px-5 py-2 rounded-lg transition-all duration-300 hover:scale-105"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Button 
            variant="ghost" 
            className="mb-6 text-slate-400 hover:text-white"
            onClick={() => navigate('/')}
          >
            <LucideIcons.ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Powerful Features for Modern Agencies
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Everything you need to create professional travel quotations, manage B2B partners, and grow your agency.
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      {categories.length > 1 && (
        <section className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white border border-white/20'
                  }`}
                >
                  {cat === 'All' ? `All ${features.length}` : cat}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Grid - New Card Design */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {filteredFeatures.map((feature, idx) => {
              const colorClass = COLOR_CLASSES[feature.tag_color] || COLOR_CLASSES.blue;
              const globalIndex = features.findIndex(f => f.id === feature.id) + 1;
              
              return (
                <div 
                  key={feature.id || idx}
                  className={`bg-white/95 backdrop-blur-sm rounded-xl border-t-4 ${colorClass.border} shadow-lg hover:shadow-xl transition-all duration-300`}
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="p-6">
                    {/* Header with Icon, Tag, and Number */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <DynamicIcon name={feature.icon} className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex items-center gap-3">
                        {feature.tag && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass.tag}`}>
                            {feature.tag}
                          </span>
                        )}
                        <span className="text-gray-400 text-sm font-medium">
                          {String(globalIndex).padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    
                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed mb-4">{feature.description}</p>
                    
                    {/* Tag Label (bottom highlight) */}
                    {feature.tag_label && (
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-opacity-10 ${colorClass.tag}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${colorClass.label.replace('text-', 'bg-')}`}></span>
                        <span className={`text-sm font-medium ${colorClass.label}`}>{feature.tag_label}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/5 rounded-3xl p-10 border border-white/10 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Agency?</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Join hundreds of travel agencies already using {siteName} to streamline their quotation process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white px-8"
                onClick={() => navigate('/register')}
              >
                Get Started Free
                <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-slate-400 text-sm">
          &copy; {settings?.copyright_text || `${new Date().getFullYear()} ${siteName}. All rights reserved.`}
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
